import { Button, Col, Divider, Flex, Input, InputNumber, Row, Select, Skeleton, Space } from "antd";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { getCorrectionTriplegDetails, saveCorrectionTripLeg } from "../../service/httpsCalls";
import TextArea from "antd/es/input/TextArea";
import { addLeadingSemicolon, removeLeadingSemicolon } from "../../helpers/commonHelpers";

const { Option } = Select;

// Centralized client-specific UI rules. Add or modify entries to control per-client behavior.
const CLIENT_UI_RULES: { [ clientId: number ]: { authOptional?: boolean; hideFields?: string[] } } = {
    // client 13: authorization optional, and hide many UI fields
    13: {
        authOptional: true,
        hideFields: [
            'memberId',
            'miscInitiatives',
            'mileage',
            'tripLegStatus',
            'levelOfServiceId',
            'treatmentTypeId',
            'puAddress',
            'puCity',
            'puState',
            'puZip',
            'puCounty',
            'doAddress',
            'doCity',
            'doState',
            'doZip',
            'doCounty',
            'memberAddress',
            'memberCity',
            'memberState',
            'memberZip',
            'memberCounty',
            'authorizationNumber',
            'attendantQty',
            'memberWeight'
        ]
    },
    // client 16: authorization optional only
    16: {
        authOptional: true,
        hideFields: [
            'memberId',
            'miscInitiatives',
            'mileage',
            'tripLegStatus',
            'levelOfServiceId',
            'treatmentTypeId',
            'puAddress',
            'puCity',
            'puState',
            'puZip',
            'puCounty',
            'doAddress',
            'doCity',
            'doState',
            'doZip',
            'doCounty',
            'memberAddress',
            'memberCity',
            'memberState',
            'memberZip',
            'memberCounty',
            'authorizationNumber',
            'attendantQty',
            'memberWeight'
        ]
    }
};

const DEFAULT_CLIENT_RULES = { authOptional: false, hideFields: [] };

export default function CorrectDetails({ selectedRec, dropdownLookup, clientId, bulkUpdate = false, disableSave=false, invoiceType, handleEvents }: any) {
    const [detailsSubmitted, setSubmitted] = useState(false);
    const [details, setDetails] = useState<any>({});
    const [saveLoader, setSaveLoader] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [memberAddressFieldVisible, setMemberAddressFieldVisible] = useState(false);

    const getClientRules = () => {       
        return CLIENT_UI_RULES[clientId] ?? DEFAULT_CLIENT_RULES;
    }

    const isAuthRequired = () => {
        const rules = getClientRules();
        return !rules.authOptional;
    }

    const isHidden = (fieldName: string) => {
        const rules = getClientRules();
        return (rules.hideFields || []).indexOf(fieldName) !== -1;
    }

    useEffect(() => {
        if (!bulkUpdate) {
            setIsLoading(true);
            getCorrectionTriplegDetails({
                clientId: selectedRec[0].clientId,
                clientInvoiceId: selectedRec[0].invoiceId,
                tripLegId: selectedRec[0].tripLegId
            }, (res) => {
                setIsLoading(false);
                if (res?.hasError) {
                    const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : 'Something went wrong!!!';
                    handleEvents({
                        isInvalid: true,
                        errorMessage: errorMsg
                    })
                } else {
                    res.nonBillableReasonCode = res?.nonBillableReasonCode
                        ? removeLeadingSemicolon(res.nonBillableReasonCode).split(';').filter((x: string) => x)
                        : null;
                    res.treatmentTypeId = res.treatmentTypeId ? res.treatmentTypeId.toString() : null;
                    res.levelOfServiceId = res.levelOfServiceId ? res.levelOfServiceId.toString() : null;
                    setDetails(res);
                }
            })
        }
    }, []);

    const clearData = () => {
        setDetails({});
        setSubmitted(false);
        handleEvents({
            close: true
        });
        selectedRec = [];
    }

    function validateData() {
        // Authorization is required only when isAuthRequired() returns true
        if (!((isHidden('treatmentTypeId') ? true : details.treatmentTypeId) 
            && (isHidden('miscInitiatives') ? true : details.miscInitiatives)
            && (isHidden('memberId') ? true : details.memberId)
            && (isHidden('mileage') ? true : details.mileage) 
            && (isHidden('levelOfServiceId') ? true : details.levelOfServiceId)
            && (isHidden('tripLegStatus') ? true : details.tripLegStatus) 
            && details.billable
            && (details.billable.toUpperCase() == 'YES' || (details.billable.toUpperCase() == 'NO' && details.nonBillableReasonCode?.length))
            && (!isAuthRequired() || details.authorizationNumber)
            && (isHidden('puAddress') ? true : details.puAddress)
            && (isHidden('puCity') ? true : details.puCity)
            && (isHidden('puState') ? true : details.puState)
            && (isHidden('puZip') ? true : details.puZip)
            && (isHidden('puCounty') ? true : details.puCounty)
            && (isHidden('doAddress') ? true : details.doAddress)
            && (isHidden('doCity') ? true : details.doCity)
            && (isHidden('doState') ? true : details.doState)
            && (isHidden('doZip') ? true : details.doZip)
            && (isHidden('doCounty') ? true : details.doCounty)
            && (isHidden('memberAddress') ? true : (!memberAddressFieldVisible || (details.memberAddress && details.memberCity && details.memberState && details.memberZip && details.memberCounty)))
        )) {
            return false;
        }
        return true;
    }

    function validateBulkUpdate() {
        if (invoiceType == "MPSS") {
            // For MPSS bulk update, allow any of these fields to be set
            const mpssFields = [
                'escorts', 'excludeFromInvoice', 'minutesWaitTime', 'cost', 'billedAmount', 'mileage', 'correctionNote'
            ];
            const anySet = mpssFields.some(f => {
                const val = (details as any)[f];
                return val !== undefined && val !== null && (typeof val === 'string' ? val.trim() !== '' : true);
            });
            // billable case with nonBillableReasonCode
            const billableSet = details?.billable && (details.billable.toUpperCase() == 'YES' || (details.billable.toUpperCase() == 'NO' && details.nonBillableReasonCode?.length));
            return anySet || billableSet;
        } else {
            // For non-MPSS bulk update, require at least one of the updatable fields to be provided
            const bulkFields = [
                'note', 'miscInitiatives', 'treatmentTypeId', 'memberId', 'mileage', 'levelOfServiceId', 'tripLegStatus',
                'authorizationNumber', 'puAddress', 'puCity', 'puState', 'puZip', 'puCounty',
                'doAddress', 'doCity', 'doState', 'doZip', 'doCounty',
                'memberAddress', 'memberCity', 'memberState', 'memberZip', 'memberCounty', 'attendantQty', 'memberWeight'
            ];
            const anySet = bulkFields.some(f => {
                const val = (details as any)[f];
                return val !== undefined && val !== null && (typeof val === 'string' ? val.trim() !== '' : true);
            });
            const billableSet = details?.billable && (details.billable.toUpperCase() == 'YES' || (details.billable.toUpperCase() == 'NO' && details.nonBillableReasonCode?.length));
            return anySet || billableSet;
        }
    }

    function getSaveObj() {
        let obj = {
            clientId: selectedRec[0].clientId,
            clientInvoiceSId: selectedRec[0].invoiceId,
            correctedTripLegs: [{
                authorizationNumber: isHidden('authorizationNumber') ? null : details.authorizationNumber,
                billable: details.billable,
                levelOfServiceId: isHidden('levelOfServiceId') ? 0 : parseInt(details.levelOfServiceId),
                memberId: isHidden('memberId') ? null : details.memberId,
                mileage: isHidden('mileage') ? 0 : details.mileage,
                miscInitiatives: isHidden('miscInitiatives') ? null : details.miscInitiatives,
                nonBillableReasonCode: details.nonBillableReasonCode?.length > 0 ? addLeadingSemicolon(details.nonBillableReasonCode.join(';')) : null,
                note: details.note,
                treatmentTypeId: isHidden('treatmentTypeId') ? 0 : parseInt(details.treatmentTypeId),
                uniqueID: details.uniqueID,
                tripLegStatus: isHidden('tripLegStatus') ? null : details.tripLegStatus,
                puAddress: isHidden('puAddress') ? null : details.puAddress,
                puCity: isHidden('puCity') ? null : details.puCity,
                puState: isHidden('puState') ? null : details.puState,
                puZip: isHidden('puZip') ? null : details.puZip,
                puCounty: isHidden('puCounty') ? null : details.puCounty,
                doAddress: isHidden('doAddress') ? null : details.doAddress,
                doCity: isHidden('doCity') ? null : details.doCity,
                doState: isHidden('doState') ? null : details.doState,
                doZip: isHidden('doZip') ? null : details.doZip,
                doCounty: isHidden('doCounty') ? null : details.doCounty,
                memberAddress: isHidden('memberAddress') ? null : details.memberAddress,
                memberCity: isHidden('memberAddress') ? null : details.memberCity,
                memberState: isHidden('memberAddress') ? null : details.memberState,
                memberZip: isHidden('memberAddress') ? null : details.memberZip,
                memberCounty: isHidden('memberAddress') ? null : details.memberCounty,
                attendantQty: isHidden('attendantQty') ? null : details.attendantQty ?? 0,
                memberWeight: isHidden('memberWeight') ? null : details.memberWeight ?? 0
            }]
        }
        return obj;
    }

    const save = () => {
        setSubmitted(true);
        if (validateData()) {
            setSaveLoader(true);
            saveCorrectionTripLeg(getSaveObj(), (res) => {
                setSaveLoader(false);
                if (res?.hasError) {
                    const errorMsg = res?.error?.messages?.length > 0 ? res.error?.messages[0].description : 'Something went wrong!!!';
                    handleEvents({
                        isInvalid: true,
                        errorMessage: errorMsg
                    })
                } else {
                    clearData();
                    handleEvents({
                        success: true,
                        showMessage: true,
                        message: 'Trip Leg Correction Details updated successfully!'
                    });
                }
            })
        }
    }

    const handleMileageChange = (inputValue, field) => {
        // Check if inputValue is valid (null if cleared)
        let obj: any = {};
        obj[field] = null;
        if (inputValue === null) {
            if (invoiceType == "MPSS" && (field == "cost" || field == "billedAmount")) {
                field == "cost" ? obj['billedAmount'] = null : '';
                obj['billedAmtCalculated'] = false;
            }
            setDetails({
                ...details,
                ...obj
            })
            return;
        }
        var valueString = inputValue.toString();
        var decimalIndex = valueString.indexOf('.');
        obj[field] = inputValue;
        if (invoiceType == "MPSS" && (field == "cost" || field == "billedAmount")) {
            field == "cost" ? obj['billedAmount'] = null : '';
            field == "billedAmount" ? obj['cost'] = null : '';
            obj['minutesWaitTime'] = null;
            obj['billedAmtCalculated'] = field == "cost";
        }
        if (decimalIndex != -1) {
            valueString = valueString.length > 9 ? valueString.slice(0, 9) : valueString;
            if (valueString.length - decimalIndex > 2) {
                valueString = parseFloat(valueString.slice(0, (decimalIndex + 3)))
            } else {
                valueString = parseFloat(valueString);
            }
            obj[field] = valueString;
            field == "cost" ? obj['billedAmount'] = (parseFloat(valueString) + (parseFloat(valueString) * 0.1)).toFixed(2) : '';
            setDetails({
                ...details,
                ...obj
            })
        } else {
            field == "cost" ? obj['billedAmount'] = (parseFloat(valueString) + (parseFloat(valueString) * 0.1)).toFixed(2) : '';
            setDetails({
                ...details,
                ...obj
            })
        }
        
    };

    const handleDecimalPoint = (e) => {
        if (e.key === ".") {
            e.preventDefault(); // Block decimal input
        }
    };

    const applyBulk = () => {
        if (validateBulkUpdate()) {
            handleEvents({
                applyChanges: true,
                data: details
            });
            reset();
        } else {
            if (details?.billable?.toUpperCase() == 'NO' && !details.nonBillableReasonCode?.length) {
                handleEvents({
                    isInvalid: true,
                    errorMessage: "Please select Non Billable Reason"
                });
            } else {
                handleEvents({
                    isInvalid: true,
                    errorMessage: "Atleast one detail needs to be updated"
                });
            }      
        }
    }

    const reset = () => {
        setDetails({})
    }
    return (
        <>
            <Skeleton loading={isLoading} active>
                {
                    invoiceType != 'MPSS' && 
                    <div className={ bulkUpdate?'bulk-update-container':'' }>
                        <Row style={{ paddingBottom: '5px', paddingTop: "0px" }}>
                                {!isHidden('memberId') && (
                                    <Col span={bulkUpdate ? 24 : 12}>
                                        <label>Member Id</label>
                                        <Input value={details?.memberId} maxLength={50} status={detailsSubmitted && !details?.memberId ? 'error' : ''} placeholder="Member Id" style={{ width: '95%' }}
                                            allowClear={bulkUpdate}
                                            onChange={(e) => {
                                                setDetails({
                                                    ...details,
                                                    memberId: e?.target?.value
                                                })
                                            }}
                                        />
                                    </Col>
                             )}
                             {!isHidden('miscInitiatives') && (
                                    <Col span={bulkUpdate ? 24 : 12}>
                                        <label>Misc Initiatives</label>
                                        <Select
                                            style={{ width: bulkUpdate ? '95%' : '100%' }}
                                            value={details?.miscInitiatives}
                                            placeholder="Select"
                                            status={detailsSubmitted && !details?.miscInitiatives ? 'error' : ''}
                                            allowClear={bulkUpdate }
                                            onChange={(value) => {
                                                setDetails({
                                                    ...details,
                                                    miscInitiatives: value
                                                })
                                            }}
                                        >
                                            <Option key="YES">Yes</Option>
                                            <Option key="NO">No</Option>
                                        </Select>
                                    </Col>
                            )}
                            </Row>
                        <Row style={{ paddingBottom: '5px', paddingTop: "0px" }}>
                {!isHidden('mileage') && (
                            <Col span={bulkUpdate ? 24 : 12}>
                                <label>Mileage</label>
                                <InputNumber<string> value={details?.mileage} maxLength={9} status={detailsSubmitted && !details?.mileage ? 'error' : ''} placeholder="Mileage" style={{ width: '95%' }}
                                    step="0.01"
                                    max="999999.99" // Maximum value allowed
                                    min="0" // Minimum value allowed
                                    onChange={(e) => handleMileageChange(e, 'mileage')}
                                />
                                        </Col>
                        )}
                {!isHidden('tripLegStatus') && (
                            <Col span={bulkUpdate ? 24 : 12}>
                                <label>Status</label>
                                <Select
                                    style={{ width: bulkUpdate ? '95%' : '100%' }}
                                    placeholder="Select Status"
                                    allowClear={bulkUpdate}
                                    status={detailsSubmitted && !details?.tripLegStatus ? 'error' : ''}
                                    onChange={(value) => {
                                        setDetails({
                                            ...details,
                                            tripLegStatus: value
                                        })
                                    }}
                                    value={details?.tripLegStatus}
                                >
                                    {dropdownLookup?.status && dropdownLookup?.status.map((item: any) => (
                                        <Option key={item.value}>{item.name}</Option>
                                    ))}
                                </Select>
                                        </Col>
                     )}
                        </Row>
                        <Row style={{ paddingBottom: '5px', paddingTop: "0px" }}>
                                {!isHidden('levelOfServiceId') && (
                                    <Col span={bulkUpdate ? 24 : 12}>
                                        <label>Level Of Service</label>
                                        <Select
                                            style={{ width: '95%' }}
                                            placeholder="Select Level Of Service"
                                            allowClear={bulkUpdate}
                                            status={detailsSubmitted && !details?.levelOfServiceId ? 'error' : ''}
                                            onChange={(value, option) => {
                                                setDetails({
                                                    ...details,
                                                    levelOfServiceId: value,
                                                    levelOfService: option.children ?? null
                                                })
                                            }}
                                            value={details?.levelOfServiceId}
                                        >
                                            {dropdownLookup?.levelOfService && dropdownLookup?.levelOfService.map((item: any) => (
                                                <Option key={item.value}>{item.name}</Option>
                                            ))}
                                        </Select>
                                    </Col>
                                )}
                                {!isHidden('treatmentTypeId') && (
                                    <Col span={bulkUpdate ? 24 : 12}>
                                        <label>Treatment Type</label>
                                        <Select
                                            style={{ width: bulkUpdate ? '95%' : '100%' }}
                                            showSearch={true}
                                            placeholder="Select Treatment Type"
                                            allowClear={bulkUpdate}
                                            status={detailsSubmitted && !details?.treatmentTypeId ? 'error' : ''}
                                            onChange={(value, option) => {
                                                setDetails({
                                                    ...details,
                                                    treatmentTypeId: value,
                                                    treatmentType: option.children ?? null
                                                })
                                            }}
                                            value={details?.treatmentTypeId}
                                            filterOption={(input, option) =>
                                                option?.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                            }
                                        >
                                            {dropdownLookup?.treatmentType && dropdownLookup?.treatmentType.map((item: any) => (
                                                <Option key={item.value}>{item.name}</Option>
                                            ))}
                                        </Select>
                                    </Col>
                            )}
                        </Row>
                        <Row style={{ paddingBottom: '5px', paddingTop: "0px" }}>
                            <Col span={bulkUpdate ? 24 : 4}>
                                <label>Billable</label>
                                <Select
                                    style={{ width: '95%' }}
                                    placeholder="Select"
                                    allowClear={bulkUpdate}
                                    status={detailsSubmitted && !details?.billable ? 'error' : ''}
                                    onChange={(value) => {
                                        if (value == "YES") {
                                            setDetails({
                                                ...details,
                                                billable: value,
                                                nonBillableReasonCode: null
                                            })
                                        } else {
                                            setDetails({
                                                ...details,
                                                billable: value
                                            })
                                        }
                                    }}
                                    value={details?.billable}
                                >
                                    <Option key="YES">Yes</Option>
                                    <Option key="NO">No</Option>
                                </Select>
                            </Col>
                            <Col span={bulkUpdate ? 24 : 20}>
                                <label>Non Billable Reason</label>
                                <Select
                                    style={{ width: bulkUpdate ? '95%' : '100%' }}
                                    mode="multiple"
                                    placeholder="Select Reason"
                                    allowClear={bulkUpdate}
                                    status={(detailsSubmitted || bulkUpdate) && details?.billable?.toUpperCase() == 'NO' && !(details?.nonBillableReasonCode?.length > 0) ? 'error' : ''}
                                    disabled={!details?.billable || details?.billable?.toUpperCase() == 'YES'}
                                    onChange={(value, option) => {
                                        const reasonDesc = option.map((x) => removeLeadingSemicolon(x.children)).filter((x) => x).join(';');
                                        setDetails({
                                            ...details,
                                            nonBillableReasonCode: value,
                                            nonBillableReasonCodeDesc: reasonDesc ? addLeadingSemicolon(reasonDesc) : null
                                        })
                                    }}
                                    value={details?.nonBillableReasonCode}
                                    optionFilterProp="label"
                                    showSearch
                                    filterOption={(input, option) =>
                                        option?.props.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                    }
                                >
                                    {dropdownLookup?.nonBillableReasons && dropdownLookup?.nonBillableReasons.map((item: any) => (
                                        <Option
                                            key={item.value}
                                            label={removeLeadingSemicolon(item.name)}
                                        >
                                            {removeLeadingSemicolon(item.name)}
                                        </Option>
                                    ))}
                                </Select>
                            </Col>
                        </Row>
                            <Row style={{ paddingBottom: '5px', paddingTop: "0px" }}>
                                {!isHidden('puAddress') && (
                                <Col span={bulkUpdate ? 24 : 5}>
                                <label>PU Address</label>
                                <Input value={details?.puAddress} maxLength={100} status={detailsSubmitted && !details?.puAddress ? 'error' : ''} placeholder="PU Address" style={{ width: '95%' }}
                                    allowClear={bulkUpdate}
                                    onChange={(e) => {
                                        setDetails({
                                            ...details,
                                            puAddress: e?.target?.value
                                        })
                                    }}
                                />
                                </Col>
                                )}

                                {!isHidden('puCity') && (
                                <Col span={bulkUpdate ? 24 : 5}>
                                <label>PU City</label>
                                <Input value={details?.puCity} maxLength={100} status={detailsSubmitted && !details?.puCity ? 'error' : ''} placeholder="PU City" style={{ width: '95%' }}
                                    allowClear={bulkUpdate}
                                    onChange={(e) => {
                                        setDetails({
                                            ...details,
                                            puCity: e?.target?.value
                                        })
                                    }}
                                />
                                </Col>
                                )}
                                {!isHidden('puState') && (
                                <Col span={bulkUpdate ? 24 : 4}>
                                <label>PU State</label>
                                <Select
                                    style={{ width: '95%' }}
                                    placeholder="Select PU State"
                                    allowClear={bulkUpdate}
                                    status={detailsSubmitted && !details?.puState ? 'error' : ''}
                                    onChange={(value) => {
                                        setDetails({
                                            ...details,
                                            puState: value
                                        })
                                    }}
                                    value={details?.puState}
                                >
                                    {dropdownLookup?.state && dropdownLookup?.state.map((item: any) => (
                                        <Option key={item.value}>{item.name}</Option>
                                    ))}
                                </Select>
                                </Col>
                                )}
                                {!isHidden('puZip') && (
                                <Col span={bulkUpdate ? 24 : 5}>
                                <label>PU Zipcode</label>
                                <Input value={details?.puZip} maxLength={10} status={detailsSubmitted && !details?.puZip ? 'error' : ''} placeholder="PU Zipcode" style={{ width: '95%' }}
                                    allowClear={bulkUpdate}
                                    onChange={(e) => {
                                        let value = e?.target?.value;

                                        // Keep only digits and dash
                                        value = value.replace(/[^0-9-]/g, "");

                                        // Auto-insert dash after 5 digits if typing continues
                                        if (value.length === 6 && !value.includes("-")) {
                                            value = value.slice(0, 5) + "-" + value.slice(5);
                                        }
                                        setDetails({
                                            ...details,
                                            puZip: value
                                        })
                                    }}
                                />
                            </Col>
                            )}
                                {!isHidden('puCounty') && (
                                <Col span={bulkUpdate ? 24 : 5}>
                                <label>PU County</label>
                                <Input value={details?.puCounty} maxLength={100} status={detailsSubmitted && !details?.puCounty ? 'error' : ''} placeholder="PU County" style={{ width: '95%' }}
                                    allowClear={bulkUpdate}
                                    onChange={(e) => {
                                        setDetails({
                                            ...details,
                                            puCounty: e?.target?.value
                                        })
                                    }}
                                />
                            </Col>
                            )}
                            </Row>
                            <Row style={{ paddingBottom: '5px', paddingTop: "0px" }}>
                                 {!isHidden('doAddress') && (
                                 <Col span={bulkUpdate ? 24 : 5}>
                                     <label>DO Address</label>
                                     <Input value={details?.doAddress} maxLength={100} status={detailsSubmitted && !details?.doAddress ? 'error' : ''} placeholder="DO Address" style={{ width: '95%' }}
                                         allowClear={bulkUpdate}
                                         onChange={(e) => {
                                             setDetails({
                                                 ...details,
                                                 doAddress: e?.target?.value
                                             })
                                         }}
                                     />
                                 </Col>
                                 )}
                                 
                                 {!isHidden('doCity') && (
                                 <Col span={bulkUpdate ? 24 : 5}>
                                     <label>DO City</label>
                                     <Input value={details?.doCity} maxLength={100} status={detailsSubmitted && !details?.doCity ? 'error' : ''} placeholder="DO City" style={{ width: '95%' }}
                                         allowClear={bulkUpdate}
                                         onChange={(e) => {
                                             setDetails({
                                                 ...details,
                                                 doCity: e?.target?.value
                                             })
                                         }}
                                     />
                                 </Col>
                                 )}
                                 {!isHidden('doState') && (
                                 <Col span={bulkUpdate ? 24 : 4}>
                                     <label>DO State</label>
                                     <Select
                                         style={{ width: '95%' }}                                       
                                         placeholder="Select DO State"
                                         allowClear={bulkUpdate}
                                         status={detailsSubmitted && !details?.doState ? 'error' : ''}
                                         onChange={(value) => {
                                             setDetails({
                                                 ...details,
                                                 doState: value
                                             })
                                         }}
                                         value={details?.doState}
                                     >
                                         {dropdownLookup?.state && dropdownLookup?.state.map((item: any) => (
                                             <Option key={item.value}>{item.name}</Option>
                                         ))}
                                     </Select>
                                 </Col>
                                 )}
                                 {!isHidden('doZip') && (
                                 <Col span={bulkUpdate ? 24 : 5}>
                                     <label>DO Zipcode</label>
                                     <Input value={details?.doZip} maxLength={10} status={detailsSubmitted && !details?.doZip ? 'error' : ''} placeholder="DO Zipcode" style={{ width: '95%' }}
                                         allowClear={bulkUpdate}
                                         onChange={(e) => {
                                             let value = e?.target?.value;

                                             // Keep only digits and dash
                                             value = value.replace(/[^0-9-]/g, "");

                                             // Auto-insert dash after 5 digits if typing continues
                                             if (value.length === 6 && !value.includes("-")) {
                                                 value = value.slice(0, 5) + "-" + value.slice(5);
                                             }
                                             setDetails({
                                                 ...details,
                                                 doZip: value
                                             })
                                         }}
                                     />
                                 </Col>
                                 )}
                                 {!isHidden('doCounty') && (
                                 <Col span={bulkUpdate ? 24 : 5}>
                                     <label>DO County</label>
                                     <Input value={details?.doCounty} maxLength={100} status={detailsSubmitted && !details?.doCounty ? 'error' : ''} placeholder="DO County" style={{ width: '95%' }}
                                         allowClear={bulkUpdate}
                                         onChange={(e) => {
                                             setDetails({
                                                 ...details,
                                                 doCounty: e?.target?.value
                                             })
                                         }}
                                     />
                                 </Col>
                                 )}
                            </Row>
                            { memberAddressFieldVisible && !isHidden('memberAddress') && <Row style={{ paddingBottom: '5px', paddingTop: "0px" }}>
                                <Col span={bulkUpdate ? 24 : 5}>
                                    <label>Member Address</label>
                                    <Input value={details?.memberAddress} maxLength={100} status={detailsSubmitted && !details?.memberAddress ? 'error' : ''} placeholder="Member Address" style={{ width: '95%' }}
                                        allowClear={bulkUpdate}
                                        onChange={(e) => {
                                            setDetails({
                                                ...details,
                                                memberAddress: e?.target?.value
                                            })
                                        }}
                                    />
                                </Col>

                                <Col span={bulkUpdate ? 24 : 5}>
                                    <label>Member City</label>
                                    <Input value={details?.memberCity} maxLength={100} status={detailsSubmitted && !details?.memberCity ? 'error' : ''} placeholder="Member City" style={{ width: '95%' }}
                                        allowClear={bulkUpdate}
                                        onChange={(e) => {
                                            setDetails({
                                                ...details,
                                                memberCity: e?.target?.value
                                            })
                                        }}
                                    />
                                </Col>
                                <Col span={bulkUpdate ? 24 : 4}>
                                    <label>Member State</label>
                                    <Select
                                        style={{ width: '95%' }}
                                        //style={{ width: bulkUpdate ? '95%' : '100%' }}
                                        placeholder="Select Member State"
                                        allowClear={bulkUpdate}
                                        status={detailsSubmitted && !details?.memberState ? 'error' : ''}
                                        onChange={(value) => {
                                            setDetails({
                                                ...details,
                                                memberState: value
                                            })
                                        }}
                                        value={details?.memberState}
                                    >
                                        {dropdownLookup?.state && dropdownLookup?.state.map((item: any) => (
                                            <Option key={item.value}>{item.name}</Option>
                                        ))}
                                    </Select>
                                </Col>
                                <Col span={bulkUpdate ? 24 : 5}>
                                    <label>Member Zipcode</label>
                                    <Input value={details?.memberZip} maxLength={10} status={detailsSubmitted && !details?.memberZip ? 'error' : ''} placeholder="Member Zipcode" style={{ width: '95%' }}
                                        allowClear={bulkUpdate}
                                        onChange={(e) => {
                                            let value = e?.target?.value;

                                            // Keep only digits and dash
                                            value = value.replace(/[^0-9-]/g, "");

                                            // Auto-insert dash after 5 digits if typing continues
                                            if (value.length === 6 && !value.includes("-")) {
                                                value = value.slice(0, 5) + "-" + value.slice(5);
                                            }
                                            setDetails({
                                                ...details,
                                                memberZip: value
                                            })
                                        }}
                                    />
                                </Col>
                                <Col span={bulkUpdate ? 24 : 5}>
                                    <label>Member County</label>
                                    <Input value={details?.memberCounty} maxLength={100} status={detailsSubmitted && !details?.memberCounty ? 'error' : ''} placeholder="Member County" style={{ width: '95%' }}
                                        allowClear={bulkUpdate}
                                        onChange={(e) => {
                                            setDetails({
                                                ...details,
                                                memberCounty: e?.target?.value
                                            })
                                        }}
                                    />
                                </Col>

                        </Row>}
                        <Row style={{ paddingBottom: '5px', paddingTop: "0px" }}>
                        {!isHidden('authorizationNumber') && (
                        <Col span={bulkUpdate ? 24 : 4}>
                            <label>Authorization #</label>
                            <Input value={details?.authorizationNumber} maxLength={50} status={detailsSubmitted && !details?.authorizationNumber ? 'error' : ''} placeholder="Authorization #" style={{ width: '95%' }}
                                allowClear={bulkUpdate}
                                onChange={(e) => {
                                    setDetails({
                                        ...details,
                                        authorizationNumber: e?.target?.value
                                    })
                                }}
                            />
                        </Col>
                                )}
                        {!isHidden('attendantQty') && (
                            <Col span={bulkUpdate ? 0 : 4}>
                                <label>Attendant Qty</label>
                                <InputNumber<number> value={details?.attendantQty ?? 0} placeholder="Attendant Qty" style={{ width: '95%' }}
                                    precision={0}
                                    step={1}
                                    max={10}
                                    min={0}
                                    onKeyDown={handleDecimalPoint}
                                    onChange={(e) => setDetails({ ...details, attendantQty: e })}
                                />
                            </Col>
                                )}
                                {!isHidden('memberWeight') && (
                                    <Col span={bulkUpdate ? 0 : 4}>
                                        <label>Member Weight</label>
                                        <InputNumber<number> value={details?.memberWeight ?? 0} placeholder="Member Weight" style={{ width: '95%' }}
                                            precision={0}
                                            step={1}
                                            max={1000}
                                            min={0}
                                            onKeyDown={handleDecimalPoint}
                                            onChange={(e) => setDetails({ ...details, memberWeight: e })}
                                        />
                                    </Col>
                                )}
                        <Col span={bulkUpdate ? 24 : 12}>
                            <label>Note</label>
                            <TextArea
                                placeholder="Enter Note"
                                autoSize={{ minRows: 2, maxRows: 6 }}
                                value={details?.note}
                                maxLength={300}
                                allowClear={bulkUpdate}
                                style={{ width: bulkUpdate ? '95%' : '100%' }}
                                onChange={(e) => setDetails({ ...details, note: e.target.value })}
                            />
                        </Col>
                    </Row>
                        </div>
                }
                {
                    invoiceType == 'MPSS' &&
                    <div className={bulkUpdate ? 'bulk-update-container' : ''}>
                        <Row style={{ paddingBottom: '5px', paddingTop: "0px" }}>
                            <Col span={bulkUpdate ? 24 : 12}>
                                <label>Exclude from the Invoice</label>
                                <Select
                                    style={{ width: bulkUpdate ? '95%' : '100%' }}
                                    value={details?.excludeFromInvoice}
                                    placeholder="Select"
                                    status={detailsSubmitted && !details?.excludeFromInvoice ? 'error' : ''}
                                    allowClear={bulkUpdate}
                                    onChange={(value) => {
                                        setDetails({
                                            ...details,
                                            excludeFromInvoice: value
                                        })
                                    }}
                                >
                                    <Option key="YES">Yes</Option>
                                    <Option key="NO">No</Option>
                                </Select>
                            </Col>
                            <Col span={bulkUpdate ? 24 : 12}>
                                <label>Mileage</label>
                                <InputNumber<string> value={details?.mileage} maxLength={9} status={detailsSubmitted && !details?.mileage ? 'error' : ''} placeholder="Mileage" style={{ width: '95%' }}
                                    step="0.01"
                                    max="999999.99" // Maximum value allowed
                                    min="0" // Minimum value allowed
                                    onChange={(e) => handleMileageChange(e, 'mileage')}
                                />
                            </Col>
                        </Row>
                        <Row style={{ paddingBottom: '5px', paddingTop: "0px" }}>
                            <Col span={bulkUpdate ? 24 : 12}>
                                <label>Wait Time (Minutes)</label>
                                    <InputNumber<string> value={details?.minutesWaitTime} maxLength={9} disabled={details?.cost || (details?.billedAmount && !details.billedAmtCalculated)} status={detailsSubmitted && !details?.minutesWaitTime ? 'error' : ''} placeholder="Wait Time (Minutes)" style={{ width: '95%' }}
                                    precision={0 }
                                    step="1"
                                    max="999999999" // Maximum value allowed
                                    min="61" // Minimum value allowed
                                    onKeyDown={handleDecimalPoint}
                                    onChange={(e) => setDetails({
                                        ...details,
                                        minutesWaitTime: e,
                                        cost: e ? null : details.cost,
                                        billedAmount: e ? parseInt(e) > 60 ? ((parseInt(e) - 60) * 1.67).toFixed(2) : null : null,
                                        billedAmtCalculated: e ? true : false
                                    })}
                                />
                            </Col>
                            <Col span={bulkUpdate ? 24 : 12}>
                                <label>Cost</label>
                                    <InputNumber<string> value={details?.cost} maxLength={9} disabled={details?.minutesWaitTime || (details?.billedAmount && !details.billedAmtCalculated)} status={detailsSubmitted && !details?.cost ? 'error' : ''} placeholder="Cost" style={{ width: '95%' }}
                                        step="0.01"
                                        max="999999.99" // Maximum value allowed
                                        min="0.01" // Minimum value allowed
                                        onChange={(e) => handleMileageChange(e, 'cost')}
                                    />
                            </Col>
                        </Row>
                        <Row style={{ paddingBottom: '5px', paddingTop: "0px" }}>
                            <Col span={bulkUpdate ? 24 : 12}>
                                    <label>{details?.cost ? 'Billed Amount = Cost + 10%' : details?.minutesWaitTime ? 'Billed Amount = (Wait Time-60) * 1.67' : 'Billed Amount' }</label>
                                    <InputNumber<string> value={details?.billedAmount} maxLength={9} disabled={details?.minutesWaitTime || details?.cost} status={detailsSubmitted && !details?.billedAmount ? 'error' : ''} placeholder="Billed Amount" style={{ width: '95%' }}
                                        step="0.01"
                                        max="999999.99" // Maximum value allowed
                                        min="0" // Minimum value allowed
                                        onChange={(e) => handleMileageChange(e, 'billedAmount')}
                                    />
                            </Col>
                                <Col span={bulkUpdate ? 24 : 12}>
                                    <label>Billable</label>
                                    <Select
                                        style={{ width: bulkUpdate ? '95%' : '100%' }}
                                        value={details?.billable}
                                        placeholder="Select"
                                        status={detailsSubmitted && !details?.billable ? 'error' : ''}
                                        allowClear={bulkUpdate}
                                        onChange={(value) => {
                                            if (value == "YES") {
                                                setDetails({
                                                    ...details,
                                                    billable: value,
                                                    nonBillableReasonCode: null
                                                })
                                            } else {
                                            setDetails({
                                                ...details,
                                                    billable: value,
                                                    nonBillableReasonCode: null
                                            })
                                            }
                                        }}
                                    >
                                        <Option key="YES">Yes</Option>
                                        <Option key="NO">No</Option>
                                    </Select>
                                </Col>
                        </Row>
                            <Row style={{ paddingBottom: '5px', paddingTop: "0px" }}>
                                <Col span={bulkUpdate ? 24 : 20}>
                                    <label>Non Billable Reason</label>
                                    <Select
                                        style={{ width: bulkUpdate ? '95%' : '100%' }}
                                        mode="multiple"
                                        placeholder="Select Reason"
                                        allowClear={bulkUpdate}
                                        status={(detailsSubmitted || bulkUpdate) && details?.billable?.toUpperCase() == 'NO' && !(details?.nonBillableReasonCode?.length > 0) ? 'error' : ''}
                                        disabled={!details?.billable || details?.billable?.toUpperCase() == 'YES'}
                                        onChange={(value, option) => {
                                            const reasonDesc = option.map((x) => removeLeadingSemicolon(x.children)).filter((x) => x).join(';');
                                            setDetails({
                                                ...details,
                                                nonBillableReasonCode: value,
                                                nonBillableReasonCodeDesc: reasonDesc ? addLeadingSemicolon(reasonDesc) : null
                                            })
                                        }}
                                        value={details?.nonBillableReasonCode}
                                        optionFilterProp="label"
                                        showSearch
                                        filterOption={(input, option) =>
                                            option?.props.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                        }
                                    >
                                        {dropdownLookup?.nonBillableReasons && dropdownLookup?.nonBillableReasons.map((item: any) => (
                                            <Option
                                                key={item.value}
                                                label={removeLeadingSemicolon(item.name)}
                                            >
                                                {removeLeadingSemicolon(item.name)}
                                            </Option>
                                        ))}
                                    </Select>
                                </Col>
                            </Row>
                            <Row style={{ paddingBottom: '5px', paddingTop: "0px" }}>
                                <Col span={bulkUpdate ? 24 : 12}>
                                    <label>Escorts</label>
                                    <InputNumber<string> value={details?.escorts} maxLength={9} status={detailsSubmitted && !details?.escorts ? 'error' : ''} placeholder="Escorts" style={{ width: '95%' }}
                                        precision={0}
                                        step="1"
                                        max="10" // Maximum value allowed
                                        min="0" // Minimum value allowed
                                        onKeyDown={handleDecimalPoint}
                                        onChange={(e) => setDetails({
                                            ...details,
                                            escorts: e
                                        })}
                                    />
                                </Col>
                                <Col span={bulkUpdate ? 24 : 12}>
                                    <label>Note</label>
                                    <TextArea
                                        placeholder="Enter Note"
                                        autoSize={{ minRows: 2, maxRows: 6 }}
                                        value={details?.correctionNote}
                                        maxLength={300}
                                        allowClear={bulkUpdate}
                                        style={{ width: bulkUpdate ? '95%' : '100%' }}
                                        onChange={(e) => setDetails({ ...details, correctionNote: e.target.value })}
                                    />
                                </Col>
                            </Row>
                    </div>
                }
                {
                    !bulkUpdate &&
                    <Flex justify="end" style={{ borderTop: "1px solid lightgray", marginTop: '0.5rem', paddingTop: '10px', marginRight: '-20px', marginLeft: '-20px' }}>
                        <Space>
                            <Button key="saveDetails" loading={saveLoader} onClick={save} type="primary">Save</Button>
                            <Button key="cancelDetails" onClick={clearData} style={{ backgroundColor: '#ffffff', borderColor: '#d9d9d9', color: 'rgba(0, 0, 0, 0.88)', marginRight:'20px' }} type="primary">
                                Cancel
                            </Button>
                        </Space>
                    </Flex>
                }
                {
                    bulkUpdate &&
                    <Divider style={{ margin: '0px' }} />
                }
                {
                    bulkUpdate &&
                    <Flex justify='center' style={{ paddingBottom: "1rem", paddingTop: "1rem", background: '#fff' }} gap='small'>
                            <Button key="applyBulk" disabled={disableSave} onClick={applyBulk} style={{ background: '#1677ff', color: '#fff' }}>
                            Apply Changes
                        </Button>
                        <Button key="resetBulkDetails" onClick={reset} style={{ backgroundColor: '#ffffff', borderColor: '#d9d9d9', color: 'rgba(0, 0, 0, 0.88)' }}>
                            Reset
                        </Button>
                    </Flex>
                }
            </Skeleton>
        </>
    )
}